import { Membership, MembershipProduct } from '@/services/MembershipService/MembershipService'

/**
 * Describes the discrete types of membership statuses that a MyMembership record can express.
 *
 * This isn't product tiers. Those are NORMAL_GUILD_MEMBER. It is the other, higher-level states
 * of membership status.
 */
export type MembershipStatus =
  | 'CANCELED_MEMBER'
  | 'EXPIRED_MEMBER'
  | 'COMPLIMENTARY_MEMBER'
  | 'GIFT_RECEIVED_ONLY_MEMBER'
  | 'INVESTOR_ONLY_MEMBER'
  | 'NORMAL_GUILD_MEMBER'
  | 'NOT_MEMBER'

/**
 * DESIGN NOTE - this is meant to be the one place where we determine, amongst
 * all options, what the membership status is.
 *
 * Where there's an interplay between the membership, product and subscription, ask
 * for membership status here. All should be considered together and in order.
 *
 * This keeps this set of rules in one location. Then, other locations
 * can switch based on the MembershipStatus union.
 */
export function mapMembershipStatus(membership: Membership | null | undefined): MembershipStatus {
  if (!isAMember(membership)) return 'NOT_MEMBER'

  if (isCanceled(membership?.primaryProduct)) return 'CANCELED_MEMBER'

  if (isInvestor(membership)) return 'INVESTOR_ONLY_MEMBER'

  if (isInactive(membership?.primaryProduct)) return 'EXPIRED_MEMBER'

  if (isComplimentary(membership)) return 'COMPLIMENTARY_MEMBER'

  if (isGift(membership?.primaryProduct)) return 'GIFT_RECEIVED_ONLY_MEMBER'

  // DESIGN NOTE - "normal" is checked last. The other membership statuses are special
  // and need to be checked first. Otherwise this would match too much.
  if (isGuildMember(membership)) return 'NORMAL_GUILD_MEMBER'

  return 'NOT_MEMBER'
}

export function isAMember(membership: Membership | null | undefined): boolean {
  return !!membership
}

export function isActiveGuildMember(membershipStatus: MembershipStatus): boolean {
  return ['COMPLIMENTARY_MEMBER', 'GIFT_RECEIVED_ONLY_MEMBER', 'INVESTOR_ONLY_MEMBER', 'NORMAL_GUILD_MEMBER'].includes(
    membershipStatus,
  )
}

function isComplimentary(membership: Membership | null | undefined): boolean {
  return !!membership && !membership.isInvestor && !!membership?.primaryProduct?.grantId
}

function isInactive(product: MembershipProduct | undefined): boolean {
  return ['EXPIRED', 'INACTIVE', 'REVOKED', 'VOIDED'].includes(product?.accessStatus ?? '')
}

function isCanceled(product: MembershipProduct | undefined): boolean {
  return product?.subscription?.status === 'CANCELLED'
}

function isGuildMember(membership: Membership | null | undefined): boolean {
  return !!membership?.active
}

function isInvestor(membership: Membership | null | undefined): boolean {
  // TODO: future: products with tier INVESTOR are expected to go away. Then this combo check can be removed.
  return !!membership?.isInvestor && (membership.primaryProduct ? membership.primaryProduct.tier === 'INVESTOR' : true)
}

function isGift(product: MembershipProduct | undefined): boolean {
  return !!product?.isGift
}
